import ContinuationNote from './ContinuationNote';
import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import useVoyagerStore from '../../stores/useVoyagerStore';
import WalkSteps from './WalkSteps';
import TierChip from './TierChip';
import TraceLink from '../agent-common/TraceLink';
import DiagnoseTrace from '../LandingAgent/DiagnoseTrace';
import VerdictEditor from './VerdictEditor';
import ContributeBox from './ContributeBox';
import { targetOfClaim, targetOfEnvelope } from './contributions';
import { confirmationAnnotation, type PanelClaim, type WalkContext } from '../../extension/capture';
import { createAnnotation, getMe, listAnnotations, type Annotation, type DiagnoseEnvelope } from '../../apis';
import { claimKey } from './claims';
import ReviewSectionRow from './ReviewSectionRow';
import { refreshedClaim, reviewSections, sectionTitle } from '../../stores/slices/reviewsSlice';

const fmtPct = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * "Why this verdict" (the mock): a side panel with the walk behind a line —
 * the verdict, the four steps as the engine ran them, the evidence bar the
 * viewer saw it through, what was set aside, and the trace. Opened from a
 * walk's bottom bar (the whole envelope) or from a review number's popover
 * (the review's stored walk, or the preview's own test).
 */
export default function VerdictPanel() {
  const opened = useVoyagerStore((s) => s.verdictPanel);
  const close = useVoyagerStore((s) => s.closeVerdictPanel);
  // A review number's panel reads its section LIVE: a refresh while it is open shows the refreshed
  // walk and value, and a confirmation binds to the walk on screen, never to the one it opened with.
  const live = useVoyagerStore((s) => (opened?.kind === 'section' && opened.section
    ? reviewSections(s.savedThreads.find((t) => t.id === opened.section!.threadId)).find((x) => x.id === opened.section!.sectionId) || null
    : null));
  const panel = opened?.kind === 'section' && opened.section && live
    ? { ...opened, title: sectionTitle(live), why: (live.why || {}) as Record<string, any>, claim: live.kind === 'claim' ? refreshedClaim(live) : null }
    : opened;
  return (
    <Drawer anchor="right" open={!!panel} onClose={close} PaperProps={{ sx: { width: 480, maxWidth: '100vw', p: { xs: 2, sm: 2.5 }, bgcolor: 'var(--surface)', backgroundImage: 'linear-gradient(180deg, var(--surface), var(--surface2))', borderLeft: '1px solid var(--line)' }, 'data-testid': 'verdict-panel' } as any}>
      {panel && (
        <Box sx={{ display: 'grid', gap: 1.5, alignContent: 'start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }} data-testid="verdict-panel-title">{panel.kind === 'section' && panel.section && !hasVerdict(panel.why) ? panel.title : 'Why this verdict'}</Typography>
            <IconButton size="small" aria-label="Close the verdict panel" onClick={close}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {panel.kind === 'section' && panel.section && <SectionDetails threadId={panel.section.threadId} sectionId={panel.section.sectionId} />}
          {panel.kind === 'envelope' ? <EnvelopeBody envelope={panel.envelope} sessionId={panel.sessionId || panel.envelope?.session_id || null} title={panel.title} />
            : (!panel.section || hasVerdict(panel.why)) && <SectionBody title={panel.title} why={panel.why} />}
          {/* a tile opened for its details alone has no verdict: nothing to confirm or contest */}
          {(panel.kind === 'envelope' || !panel.section || hasVerdict(panel.why)) && <VerdictActions key={actionKey(panel)} panel={panel} />}
        </Box>
      )}
    </Drawer>
  );
}

/** A stored walk, a significance test, or a preview: something the panel can call a verdict. A tile with none shows its details alone. */
function hasVerdict(why: Record<string, any> | null | undefined): boolean {
  return !!(why?.verdict?.headline || why?.significance || why?.preview?.headline);
}

/** A pinned number's details (#317), read live from the review so an unpin or a saved note shows at once. */
function SectionDetails({ threadId, sectionId }: { threadId: string; sectionId: string }) {
  const section = useVoyagerStore((s) => reviewSections(s.savedThreads.find((t) => t.id === threadId)).find((x) => x.id === sectionId) || null);
  const close = useVoyagerStore((s) => s.closeVerdictPanel);
  // a save from here (a note, an unpin) that failed says so here — the page's alert is behind the drawer
  const error = useVoyagerStore((s) => s.reviewsError);
  useEffect(() => { if (!section) close(); }, [section, close]);   // unpinned while open: nothing left to show
  if (!section) return null;
  return (
    <Box data-testid="review-tile-details" sx={{ display: 'grid', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>Where this came from</Typography>
      <ReviewSectionRow threadId={threadId} section={section} variant="panel" />
      {error && <Alert severity="error" onClose={() => useVoyagerStore.setState({ reviewsError: null } as any)} data-testid="review-panel-error">{error}</Alert>}
    </Box>
  );
}

/** One VerdictActions per verdict: the panel switching straight to another walk or number starts its say afresh. */
function actionKey(panel: NonNullable<ReturnType<typeof useVoyagerStore.getState>['verdictPanel']>): string {
  if (panel.kind === 'envelope') return `walk:${panel.sessionId || panel.envelope?.session_id || ''}:${panel.envelope?.target?.metric || ''}:${panel.turnId || ''}`;
  return `section:${panel.why?.session_id || panel.why?.preview?.session_id || ''}:${panel.claim?.id || panel.title}`;
}

/**
 * What the reader can DO about the verdict (the mock): confirm it — a signed
 * record that this verdict is right, on the walk that produced it; contest it
 * — the app's correction, the true driver named; or add a missing event — a
 * contribution the engine did not see, dry-run before it counts. Each is a
 * person's act, signed by the server, bound to the number's metric, window
 * and perspective, and written into the walk's own session.
 */
function VerdictActions({ panel }: { panel: NonNullable<ReturnType<typeof useVoyagerStore.getState>['verdictPanel']> }) {
  const correctWhyTurn = useVoyagerStore((s) => s.correctWhyTurn);
  const contributeWhyTurn = useVoyagerStore((s) => s.contributeWhyTurn);
  const openVerdictPanel = useVoyagerStore((s) => s.openVerdictPanel);
  const [mode, setMode] = useState<'idle' | 'contest' | 'event'>('idle');
  const [confirmDriver, setConfirmDriver] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState<Annotation | null>(null);
  const [others, setOthers] = useState<Annotation[]>([]);
  // what is on record must be known before a confirmation can be made: loading → ready, or failed (retryable)
  const [record, setRecord] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [reload, setReload] = useState(0);
  const [corrected, setCorrected] = useState<Annotation | null>(null);
  const [added, setAdded] = useState<Annotation | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The number the acts bind to, and the walk they are said on.
  const envelope: DiagnoseEnvelope | null = panel.kind === 'envelope' ? panel.envelope : sectionEnvelope(panel.claim, panel.why);
  const perspective = panel.perspective || (panel.kind === 'section' ? panel.claim?.grounding?.perspective : envelope?.target && (envelope as any).target.perspective) || null;
  const sessionId = panel.kind === 'envelope' ? (panel.sessionId || panel.envelope?.session_id || null) : (panel.why?.session_id || panel.why?.preview?.session_id || null);
  const claim: PanelClaim | null = panel.kind === 'section'
    ? (panel.claim ? { id: String(panel.claim.id || 'section'), label: String(panel.claim.label || panel.title), grounding: panel.claim.grounding } : null)
    : (envelope?.target?.metric ? { id: `walk:${envelope.target.metric}`, label: String(envelope.target.metric).split('.').pop() || envelope.target.metric,
        grounding: { metric_qid: envelope.target.metric, perspective, window: { start: envelope.target.window_start, end: envelope.target.window_end },
          // a root AND/OR group is one slice and travels as the group it is — flattened to [] it would confirm the whole
          filters: Array.isArray(envelope.target.filters) ? envelope.target.filters : (envelope.target.filters && typeof envelope.target.filters === 'object' && Object.keys(envelope.target.filters).length ? envelope.target.filters : []) } } : null);
  const walk: WalkContext = { sessionId, verdict: envelope?.verdict ? { kind: envelope.verdict.kind, headline: envelope.verdict.headline } : null,
    drivers: (envelope?.drivers || []) as any };
  const topDriver = (envelope?.drivers || [])[0] || null;
  // a contribution binds to the walk it was said on, for a review number too: the review's stored walk, or the preview's
  const sectionTarget = panel.kind === 'section' && panel.claim && perspective ? targetOfClaim(panel.claim, perspective) : null;
  const target = panel.kind === 'envelope' ? (envelope && perspective ? targetOfEnvelope(envelope, perspective, sessionId) : null)
    : (sectionTarget ? { ...sectionTarget, sessionId } : null);
  const key = claim ? claimKey((panel.kind === 'section' && panel.claim ? panel.claim : claim) as any) : null;
  // A confirmation is immutable and the panel is reopened often: what is
  // already on record for THIS verdict (the same walk) is loaded first, so
  // the same person cannot confirm it twice, and everyone else's shows.
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setRecord('loading');
    // both lookups must succeed: a record that could not be read is not an empty record
    Promise.all([getMe(), listAnnotations({ kind: 'confirmation', source_ref: key, limit: 50 })])
      .then(([me, rows]) => {
        if (cancelled) return;
        // the same walk exactly — a verdict with no session matches only a confirmation with none, never an older walk's —
        // and the verdict as it reads now: a confirmation of the walk's original verdict does not stand for its corrected one
        const headline = walk.verdict?.headline || null;
        const ofThisVerdict = (a: Annotation) => a.source_ref === key
          && (((a.provenance as any)?.confirmation?.session_id || null) === (sessionId || null))
          && (!headline || !(a.provenance as any)?.confirmation?.headline || (a.provenance as any).confirmation.headline === headline);
        const onThisWalk = rows.filter(ofThisVerdict);
        // all of my own records on this walk: the verdict, and — a separate act — its driver; the one that says more is shown
        const mine = onThisWalk.filter((a) => (a.provenance as any)?.asserted_by === me.id);
        const shown = mine.find((a) => (a.provenance as any)?.confirmation?.confirms_driver) || mine[0] || null;
        // a confirmation made while the record was loading stands — only while it is of the verdict now shown:
        // one of a verdict since corrected does not
        setConfirmed((current) => {
          const standing = current && ofThisVerdict(current) ? current : null;
          return standing && (standing.provenance as any)?.confirmation?.confirms_driver ? standing : shown || standing;
        });
        setOthers(onThisWalk.filter((a) => !mine.includes(a)));
        setRecord('ready');
      })
      .catch(() => { if (!cancelled) setRecord('failed'); });
    return () => { cancelled = true; };
  }, [key, sessionId, reload, walk.verdict?.headline]);
  if (!claim || !perspective) return null;
  // What the number can carry: a confirmation binds to its target the way a
  // correction does — a row that stands for a period has none, and offers nothing.
  const confirmable = !!confirmationAnnotation(claim, walk, {});
  const contestable = !!envelope;

  const confirm = async (driverToo: boolean = confirmDriver) => {
    const body = confirmationAnnotation(claim, walk, { confirmsDriver: driverToo });
    if (!body || confirming) return;
    setConfirming(true); setError(null);
    try {
      // bound to the number by its key too, so the popover lists who confirmed it beside the notes
      const saved = await createAnnotation({ ...body, source_ref: key });
      setConfirmed(saved);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not save the confirmation');
    } finally {
      setConfirming(false);
    }
  };
  const who = (a: Annotation | null) => (a?.provenance as any)?.asserted_by_name || 'you';

  return (
    <Box data-testid="verdict-actions" sx={{ display: 'grid', gap: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>Your say</Typography>
      {confirmed && <Typography sx={{ fontSize: 13 }} data-testid="verdict-confirmed">✓ Confirmed by {who(confirmed)} · {String((confirmed.provenance as any)?.asserted_at || new Date().toISOString()).slice(0, 10)}{(confirmed.provenance as any)?.confirmation?.confirms_driver ? ' · driver confirmed too' : ''}</Typography>}
      {others.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="verdict-confirmed-others">
          {confirmed ? 'Also confirmed' : 'Confirmed'} by {others.map((a) => `${who(a)} · ${String((a.provenance as any)?.asserted_at || '').slice(0, 10)}`).join(', ')}
        </Typography>
      )}
      {corrected && <Typography sx={{ fontSize: 13 }} data-testid="verdict-contested">✎ Contested by {who(corrected)} — {corrected.title}{panel.kind === 'section' ? ' · the review reads the corrected verdict on its next refresh' : ''}</Typography>}
      {added && <Typography sx={{ fontSize: 13 }} data-testid="verdict-event-added">＋ Added by {who(added)} — {added.title}</Typography>}
      {mode === 'idle' && !confirmable && !contestable && !target && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="verdict-no-target">
          This number stands for a period rather than a slice of the metric: there is no one verdict here to confirm or contest.
        </Typography>
      )}
      {mode === 'idle' && (confirmable || contestable || target) && (
        <>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {confirmable && <Button size="small" variant="contained" disabled={confirming || !!confirmed || record !== 'ready' || (panel.kind === 'section' && !!corrected)} onClick={() => confirm()} data-testid="verdict-confirm">{confirmed ? 'Confirmed' : confirming ? 'Confirming…' : record === 'loading' ? 'Checking…' : 'Confirm'}</Button>}
            {confirmable && confirmed && topDriver && !(confirmed.provenance as any)?.confirmation?.confirms_driver && (
              // the verdict is confirmed; the cause is a separate act, still open
              <Button size="small" variant="outlined" disabled={confirming} onClick={() => confirm(true)} data-testid="verdict-confirm-driver">{confirming ? 'Confirming…' : 'Also confirm the driver'}</Button>
            )}
            {contestable && <Button size="small" variant="outlined" disabled={!!corrected} onClick={() => setMode('contest')} data-testid="verdict-contest">Contest</Button>}
            {target && <Button size="small" variant="outlined" onClick={() => setMode('event')} data-testid="verdict-add-event">Add a missing event</Button>}
          </Box>
          {confirmable && topDriver && !confirmed && (
            <FormControlLabel sx={{ ml: 0 }} control={<Checkbox size="small" checked={confirmDriver} onChange={(e) => setConfirmDriver(e.target.checked)} inputProps={{ 'aria-label': 'Also confirm the driver' } as any} />}
              label={<Typography variant="caption">Also confirm the driver — confirming the number does not confirm the cause</Typography>} />
          )}
          {record === 'failed' && !confirmed && (
            <Typography variant="caption" color="error" data-testid="verdict-record-failed">
              Could not read what is already on record for this verdict.{' '}
              <Button size="small" variant="text" onClick={() => setReload((n) => n + 1)} sx={{ py: 0, minHeight: 0, fontSize: '0.72rem' }} data-testid="verdict-record-retry">Try again</Button>
            </Typography>
          )}
          {error && <Typography variant="caption" color="error" data-testid="verdict-error">{error}</Typography>}
        </>
      )}
      {mode === 'contest' && envelope && (
        <VerdictEditor envelope={envelope} perspective={perspective} sessionId={sessionId}
          onCancel={() => setMode('idle')}
          onSaved={(saved) => {
            setMode('idle'); setCorrected(saved);
            if (panel.kind === 'envelope' && panel.turnId) {
              // the walk's verdict changed: the panel reads the corrected envelope, so a
              // confirmation made next is of the verdict now shown, never the replaced one —
              // and a confirmation of the replaced verdict no longer stands as this one's
              setConfirmed(null); setOthers([]);
              correctWhyTurn(panel.turnId, saved);
              const turn = useVoyagerStore.getState().landingAgentTurns.find((t) => t.id === panel.turnId);
              if (turn?.result?.diagnose) openVerdictPanel({ ...panel, envelope: turn.result.diagnose });
            }
          }} />
      )}
      {mode === 'event' && target && (
        <ContributeBox target={target} driver={null}
          onSaved={(saved) => { setAdded(saved); if (panel.kind === 'envelope' && panel.turnId) contributeWhyTurn(panel.turnId, saved); }}
          onValidated={(saved) => { setAdded(saved); if (panel.kind === 'envelope' && panel.turnId) contributeWhyTurn(panel.turnId, saved); }}
          onCancel={() => setMode('idle')} />
      )}
    </Box>
  );
}

/** A review section's stored walk as the envelope a correction or a confirmation is made on: its target and verdict, its driver as the one candidate. */
function sectionEnvelope(claim: Record<string, any> | null | undefined, why: Record<string, any>): DiagnoseEnvelope | null {
  // the number's own target — the query's filters AND the row's dimensions, the window half-open — the way a
  // confirmation and a contribution bind to it; a row that stands for a period has no target, so no envelope
  const target = targetOfClaim(claim, claim?.grounding?.perspective || null);
  if (!target) return null;
  // the stored driver as the editor names one: a label for a metric or a segment, a title (and no date) for an event
  const driver = why?.driver?.id || why?.driver?.label ? [{ kind: why.driver.kind || 'text', id: why.driver.id || why.driver.label, label: why.driver.label || why.driver.id,
    title: why.driver.label || why.driver.id, ts: '', tier: why.driver.tier || 'unconfirmed', sources: [],
    // a segment is the segment OF a metric (#297): the editor names driver_metric from it, and the cap it carried
    ...(why.driver.kind === 'segment' && why.driver.metric ? { metric: why.driver.metric } : {}), ...(why.driver.capped_by ? { capped_by: why.driver.capped_by } : {}) }] : [];
  return {
    target: { metric: target.metric, window_start: target.window_start, window_end: target.window_end, filters: (target.filters || []) as any },
    verdict: why?.verdict?.headline ? { kind: why.verdict.kind || 'explained', headline: why.verdict.headline, confidence: 0 }
      : { kind: (why?.significance?.status === 'within_normal' ? 'within_normal' : 'preview') as any, headline: why?.preview?.headline || 'the significance test', confidence: 0 },
    chain: [], segments: null, annotations: [], alternatives: [], residual_note: null, budget: { limit: 0, queries: 0, effort: 'quick' },
    drivers: driver as any, session_id: why?.session_id || null,
  } as DiagnoseEnvelope;
}

function EnvelopeBody({ envelope, sessionId, title }: { envelope: any; sessionId: string | null; title?: string | null }) {
  const verdict = envelope?.verdict || {};
  const policy = envelope?.policy;
  const sig = (envelope?.steps || []).find((s: any) => s?.id === 'significance')?.detail || {};
  const alternatives: any[] = envelope?.alternatives || [];
  return (
    <>
      {title && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{title}</Typography>}
      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'var(--ground)' }} data-testid="verdict-panel-verdict">
        <Typography variant="overline" sx={{ letterSpacing: 1 }}>{String(verdict.kind || '').replace(/_/g, ' ')} · confidence {verdict.confidence}</Typography>
        <Typography sx={{ fontWeight: 600, mt: 0.25 }}>{verdict.headline}</Typography>
        {typeof sig.z === 'number' && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
            z = {sig.z.toFixed(1)}{typeof sig.threshold === 'number' ? ` vs threshold ${sig.threshold.toFixed(1)}` : ''}{typeof sig.trailing === 'number' ? ` over ${sig.trailing} trailing windows` : ''}{sig.delta_pct != null ? ` · ${fmtPct(sig.delta_pct)} vs prior period` : ''}
          </Typography>
        )}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>The walk</Typography>
        <WalkSteps steps={envelope?.steps} />
      </Box>
      {policy && (
        <Box data-testid="verdict-panel-policy">
          <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>Evidence bar</Typography>
          <Typography sx={{ fontSize: 13 }}>
            {policy.policy === 'gated' ? `Gated at ${cap(String(policy.bar || ''))}${policy.raised ? ' (raised for this answer)' : ''}` : policy.policy === 'facts' ? 'Facts only' : 'Everything, tiers shown'}
            {typeof policy.hidden_count === 'number' && policy.hidden_count > 0 ? ` · ${policy.hidden_count} driver${policy.hidden_count === 1 ? '' : 's'} below the bar` : ''}
          </Typography>
        </Box>
      )}
      {alternatives.length > 0 && (
        <Box data-testid="verdict-panel-alternatives">
          <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>Considered and set aside</Typography>
          {alternatives.map((a, i) => (
            <Typography key={i} sx={{ fontSize: 13, lineHeight: 1.6 }}><b>{a.hypothesis}</b> — {a.status}{a.reason ? `: ${a.reason}` : ''}</Typography>
          ))}
        </Box>
      )}
      {envelope?.budget && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {envelope.budget.queries} of {envelope.budget.limit} queries · {envelope.budget.effort} effort
        </Typography>
      )}
      {sessionId && <Box><TraceLink sessionId={sessionId} title="the full trace behind this walk" /></Box>}
      <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
        <DiagnoseTrace envelope={envelope} />
      </Box>
    </>
  );
}

function SectionBody({ title, why }: { title: string; why: Record<string, any> }) {
  const sig = why?.significance || {};
  const preview = why?.preview;
  return (
    <>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{title}</Typography>
      {why?.verdict?.headline ? (
        <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'var(--ground)' }} data-testid="verdict-panel-verdict">
          <Typography variant="overline" sx={{ letterSpacing: 1 }}>{String(why.verdict.kind || '').replace(/_/g, ' ')}</Typography>
          <Typography sx={{ fontWeight: 600, mt: 0.25 }}>{why.verdict.headline}</Typography>
          {why.driver?.label && (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.75 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>driver:</Typography>
              <Chip size="small" variant="outlined" label={why.driver.label} sx={{ height: 20, fontSize: '0.68rem' }} />
              <TierChip tier={why.driver.tier} />
            </Box>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.75 }}>From the review's last refresh. The full walk re-runs it with today's data.</Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'var(--ground)' }} data-testid="verdict-panel-verdict">
          <Typography sx={{ fontSize: 13 }}>
            The verdict is the significance test alone
            {typeof sig.z === 'number' ? `: z = ${sig.z.toFixed(1)}` : ''}
            {typeof sig.threshold === 'number' ? ` against a threshold of ${sig.threshold.toFixed(1)}` : ''}
            {sig.delta_pct != null ? `, ${fmtPct(sig.delta_pct)} vs the prior period` : ''}
            . A cause is named only by the full walk.
          </Typography>
        </Box>
      )}
      <ContinuationNote continuation={why?.continuation || preview?.continuation} />
      {preview?.headline && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{preview.headline}</Typography>}
      {(why?.session_id || preview?.session_id) && (
        <Box><TraceLink sessionId={why?.session_id || preview?.session_id} title={why?.session_id ? 'the walk behind this verdict' : 'the preview behind this line'} /></Box>
      )}
    </>
  );
}
